import { ZodType } from 'zod';
import { supportClearinghousePayerSchema, supportPayerSchema } from './payer.contract';

export interface IContractEntry {
    /** Stable name used in logs and telemetry. */
    name: string;
    /** Matches the request path (query string already stripped). */
    match: RegExp;
    /** Schema for a single record. List responses are checked element-wise. */
    schema: ZodType;
}

/**
 * Which responses get checked.
 *
 * Deliberately an allow-list rather than a catch-all. Validating all 56 API classes would
 * cost a JSON re-parse on every request in the app for no benefit until someone has written
 * a schema worth checking against. Payers is the pilot; add entries as schemas are written.
 *
 * Paths are the Support surface: `/support/payer`, `/support/clearinghousepayer`. The
 * trailing `(/[^/]+)?$` lets one entry cover both the list and the by-id route.
 *
 * ## Before adding a patient-bearing resource
 *
 * A drift report leaves the browser and lands in telemetry. The middleware strips the query
 * string (filters there carry patient and subscriber ids) and reports only field paths and
 * zod's message — but zod's message for an enum mismatch can quote the *received value*.
 * On payer resources that value is a coverage or transaction code and is not sensitive.
 * On `/support/patient`, `/support/subscriber`, `/support/guarantor` or anything else
 * carrying PHI, it could be. Do not register those without first constraining what the
 * reporter is allowed to emit.
 */
export const CONTRACT_REGISTRY: IContractEntry[] = [
    {
        name: 'supportPayer',
        match: /\/support\/payer(\/[^/]+)?$/,
        schema: supportPayerSchema,
    },
    {
        name: 'supportClearinghousePayer',
        match: /\/support\/clearinghousepayer(\/[^/]+)?$/,
        schema: supportClearinghousePayerSchema,
    },
];

export function findContract(path: string): IContractEntry | undefined {
    return CONTRACT_REGISTRY.find(entry => entry.match.test(path));
}
